/**
 * 通讯器 · TA 的朋友（新版列表，UF-01「宠物交流」）：数据来自 GET /pets/{pet_id}/friends。
 * 替下 ./FriendsSection（旧的 GET /friends：按同地次数叫“好朋友”，c84a D1402 P3 已定不再这样叫）：通讯器“TA 的朋友”分区挂的是这一个；
 * 旧组件与旧接口的退役等 I 的 P3 小包与迁移证据另定。
 *
 * 和旧列表的不同：
 * - 顺序照后端给的（最近往来的在前），不再按见面次数重排；
 * - 每位朋友的标签是“有没有来回”的四档（聊过 / 打过招呼 / 没接上 / 遇见过），不按次数推“初识 / 熟人 / 好朋友”；
 * - 关系只写“已不是朋友”（确曾有关系、后来解除）；两边关系都在也不写“是朋友”（不代表聊过）；
 * - 两种次数单独一行：“同地 N 次 · 当面 N 次”（c84a D1736-4，2531 R25 写法 A），不相加、不写“其中”；0 次与不知道都不写，星球居民没有当面；
 *   读屏念完整说法（同一时间在同一个地方 N 次，面对面遇上 N 次）；“最近往来”只是日期，不说此刻在哪；
 * - 每位朋友（宠物和星球居民都有）都点进详情；
 * - 朋友太多只列了最近往来的 200 位时，末尾说一句。
 * 没有加、删、批准朋友的按钮：交朋友是宠物自己的事，主人只看。
 * 头像只是装饰（aria-hidden，名字紧挨着就写了）；PetFriendSummary 没有照片字段，live 下是中性爪印。
 */
import { Link } from "react-router";
import { toApiError } from "@/shared/api/errors";
import { env } from "@/shared/config/env";
import { Chip, EmptyState, ErrorState, Icon, LoadingState } from "@/shared/ui";
import { PetPortrait } from "@/features/pets/PetPortrait";
import { friendDetailHref, friendRow, type FriendRowView } from "./petFriends";
import { FriendParts } from "./FriendParts";
import { usePetFriendList } from "./usePetFriends";
import "./communicator.css";
import "./petFriends.css";

function FriendRow({ row }: { row: FriendRowView }) {
  const href = friendDetailHref(row.id);
  const content = (
    <>
      <span className="ps-friend__avatar" aria-hidden="true">
        <PetPortrait name={row.name} size={48} />
      </span>
      <div className="ps-friend__main">
        <div className="ps-friend__title">
          <strong className="ps-friend__name">{row.name}</strong>
          {row.exchange ? <Chip tone={row.exchange.tone}>{row.exchange.label}</Chip> : null}
        </div>
        {row.resident || row.relation || row.facts.length ? (
          <div className="ps-friend__facts">
            {row.resident ? (
              <Chip tone="sun" icon="planet">
                星球居民
              </Chip>
            ) : null}
            {row.relation ? <Chip>{row.relation}</Chip> : null}
            {row.facts.length ? <span><FriendParts parts={row.facts} /></span> : null}
          </div>
        ) : null}
        {row.counts ? <div className="ps-friend__counts"><FriendParts parts={row.counts.split(" · ")} /></div> : null}
        {row.when ? <div className="ps-friend__seen">{row.when}</div> : null}
      </div>
    </>
  );
  if (!href) {
    return (
      <li className="ps-friend" data-friend-kind={row.resident ? "resident" : "pet"}>
        {content}
      </li>
    );
  }
  return (
    <li>
      <Link className="ps-friend ps-friend--link" data-friend-kind={row.resident ? "resident" : "pet"} to={href} aria-label={row.spoken}>
        {content}
        <Icon name="chevron" size={16} className="ps-friend__go" />
      </Link>
    </li>
  );
}

export function PetFriendList({ petId }: { petId: string | null }) {
  const friends = usePetFriendList(petId);

  if (friends.isError) {
    const error = toApiError(friends.error);
    if (error.isCapabilityUnavailable || error.unknownRoute) {
      return env.dataMode === "fixture" ? (
        <EmptyState icon="info" title="演示里还没有朋友记录">
          朋友要 TA 真的在外面遇见，才会记在这里；演示世界里先空着。
        </EmptyState>
      ) : (
        <EmptyState icon="info" title="暂时看不到 TA 的朋友">
          过一会儿再来看看。
        </EmptyState>
      );
    }
    if (error.isNotFound) {
      return (
        <EmptyState icon="lock" title="这里看不到了">
          你可能已经不在这个家里，或者这只宠物的内容没有向你开放。
        </EmptyState>
      );
    }
    return <ErrorState error={friends.error} onRetry={() => void friends.refetch()} retrying={friends.isFetching} />;
  }
  if (!friends.data) return <LoadingState lines={2} label="翻看 TA 的朋友…" />;
  const items = friends.data.items ?? [];
  if (items.length === 0) {
    return (
      <EmptyState icon="heart" title="TA 还没在外面遇到朋友">
        TA 出门时，也许会遇见新朋友。
      </EmptyState>
    );
  }
  const rows = items.map((friend) => friendRow(friend));
  return (
    <div className="ps-friends-section">
      <ul className="ps-friends" aria-label="TA 的朋友">
        {rows.map((row) => (
          <FriendRow key={row.id} row={row} />
        ))}
      </ul>
      {rows.some((row) => row.resident) ? <p className="ps-friends__note">标着“星球居民”的，是一直住在这颗星球上的居民，不是别人家的宠物。</p> : null}
      {friends.data.truncated ? <p className="ps-friends__note">朋友有点多，这里先列最近往来的 200 位。</p> : null}
    </div>
  );
}
